import React, { useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    Modal,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ArrowForward, AutoStories, CheckCircle, Email, LocationOn, Phone } from '@mui/icons-material';
import { styled } from '@mui/system';
import skylarkReadsLogo from '../assets/skylark-reads-logo.jpg';

const ModalContainer = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(420px, calc(100% - 32px))',
    padding: theme.spacing(3),
    textAlign: 'center',
    outline: 'none',
    borderRadius: '12px',
}));

const inquiryTypes = [
    'Skylark Reads product enquiry',
    'General business enquiry',
    'Services enquiry',
    'Careers enquiry',
];

const productHighlights = [
    'School reading program demos',
    'Student assessment and reading level queries',
    'Physical book and library workflow discussions',
    'Parent, teacher, and school dashboards',
];

const Contact = () => {
    const mapTitle = 'Epytome IT location map';
    const [inquiryType, setInquiryType] = useState(inquiryTypes[0]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [consent, setConsent] = useState(false);
    const [modal, setModal] = useState({ open: false, title: '', message: '' });

    const openModal = (title, message) => {
        setModal({ open: true, title, message });
    };

    const closeModal = () => {
        setModal({ open: false, title: '', message: '' });
    };

    const handleProductQuery = () => {
        setInquiryType(inquiryTypes[0]);
        setMessage(
            'I would like to know more about Skylark Reads for our school/reading program. Please share product details, demo availability, and next steps.'
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!consent) {
            openModal('Consent Required', 'You must consent to be contacted.');
            return;
        }
        if (!isValidEmail(email)) {
            openModal('Invalid Email', 'Please enter a valid email address.');
            return;
        }
        if (!isValidPhone(phone)) {
            openModal('Invalid Phone Number', 'Please enter a valid phone number.');
            return;
        }

        try {
            const response = await fetch(
                'https://szj75prtbb.execute-api.ap-south-1.amazonaws.com/SendContactEmail',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ inquiryType, name, email, phone, message }),
                }
            );

            if (response.ok) {
                openModal(
                    'Thank You for Reaching Out!',
                    <>
                        <p>We’ve received your message. A member of our team will contact you shortly with an update.</p>
                        <p>Next Steps:</p>
                        <ul>
                            <li>Keep an eye on your email for our response.</li>
                            <li>Check your spam/junk folder just in case.</li>
                            <li>We appreciate your patience and understanding!</li>
                        </ul>
                    </>
                );
                handleClear();
            } else {
                const error = await response.json();
                openModal('Error', error.message || 'Failed to send the message.');
            }
        } catch (err) {
            console.error(err);
            openModal('Error', 'An error occurred while sending your message.');
        }
    };

    const handleClear = () => {
        setInquiryType(inquiryTypes[0]);
        setName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setConsent(false);
    };

    const isValidEmail = (value) => {
        return value.includes('@') && value.includes('.');
    };

    const isValidPhone = (value) => {
        return /^\d+$/.test(value) && value.length >= 10;
    };

    return (
        <Box sx={{ background: 'linear-gradient(135deg, #f7fbff 0%, #edf8fb 48%, #ffffff 100%)' }}>
            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={5}>
                        <Stack spacing={3} sx={{ height: '100%' }}>
                            <Box>
                                <Typography variant="h2" component="h1" sx={{ color: 'primary.main', fontSize: { xs: '2.5rem', md: '3.8rem' }, lineHeight: 1.08 }}>
                                    Let’s talk about what you’re building
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mt: 2.5, fontSize: '1.08rem', lineHeight: 1.8 }}>
                                    Reach out for Skylark Reads, technology services, partnerships, or careers. Tell us what you need and we’ll route your enquiry to the right team.
                                </Typography>
                            </Box>

                            <Card sx={{ border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 20px 60px rgba(16,32,51,0.08)' }}>
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box
                                            component="img"
                                            src={skylarkReadsLogo}
                                            alt="Skylark Reads logo"
                                            sx={{ width: 84, height: 84, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }}
                                        />
                                        <Box>
                                            <Typography variant="h5">Product enquiry</Typography>
                                            <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                Interested in Skylark Reads for your school or reading program?
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Divider sx={{ my: 3 }} />
                                    <Stack spacing={1.5}>
                                        {productHighlights.map((item) => (
                                            <Stack direction="row" spacing={1.5} alignItems="center" key={item}>
                                                <CheckCircle color="success" fontSize="small" />
                                                <Typography sx={{ color: 'text.secondary' }}>{item}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Button variant="contained" fullWidth endIcon={<ArrowForward />} sx={{ mt: 3 }} onClick={handleProductQuery}>
                                        Start a Skylark Reads query
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card sx={{ background: '#0b1727', color: 'white', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={2}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <AutoStories color="secondary" />
                                            <Typography variant="h6">For product demos and school onboarding</Typography>
                                        </Stack>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.7 }}>
                                            Use the product enquiry option and include your school name, expected student count, and what you want to evaluate.
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Card sx={{ height: '100%', border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 24px 70px rgba(16,32,51,0.1)' }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography variant="h4" component="h2">
                                    Send us a message
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
                                    We usually need just a few details to understand the request.
                                </Typography>
                                <form onSubmit={handleSubmit}>
                                    <Grid container spacing={2.5}>
                                        <Grid item xs={12}>
                                            <TextField
                                                select
                                                fullWidth
                                                label="Enquiry type"
                                                value={inquiryType}
                                                onChange={(e) => setInquiryType(e.target.value)}
                                            >
                                                {inquiryTypes.map((type) => (
                                                    <MenuItem value={type} key={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField fullWidth label="Phone" required value={phone} onChange={(e) => setPhone(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Message"
                                                multiline
                                                rows={5}
                                                required
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                placeholder="Tell us about your school, product query, service requirement, or partnership idea."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} color="primary" />}
                                                label={
                                                    <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                                                        I consent for my personal data to be processed so Epytome IT can respond to this enquiry.
                                                    </Typography>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                                                <Button variant="outlined" onClick={handleClear}>
                                                    Clear
                                                </Button>
                                                <Button variant="contained" type="submit" endIcon={<ArrowForward />}>
                                                    Send Message
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </form>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 4 }}>
                    {[
                        { icon: <Email />, title: 'Email us', text: 'Use the form and we’ll route your enquiry internally.' },
                        { icon: <Phone />, title: 'Product calls', text: 'Ask for a Skylark Reads demo or school onboarding discussion.' },
                        { icon: <LocationOn />, title: 'Location', text: 'Vadavalli, Coimbatore, Tamil Nadu.' },
                    ].map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <Card sx={{ height: '100%', border: '1px solid rgba(15,47,95,0.08)', boxShadow: 'none' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ color: 'secondary.main', display: 'flex', mb: 1.5 }}>{item.icon}</Box>
                                    <Typography variant="h6">{item.title}</Typography>
                                    <Typography sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.6 }}>{item.text}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                <Card sx={{ mt: 4, overflow: 'hidden', border: '1px solid rgba(15,47,95,0.08)', boxShadow: '0 18px 45px rgba(16,32,51,0.06)' }}>
                    <Box sx={{ width: '100%', height: { xs: 300, md: 360 } }}>
                        <iframe
                            title={mapTitle}
                            width="100%"
                            height="100%"
                            style={{ border: 0, display: 'block' }}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31329.264184660784!2d76.88517267407393!3d11.026768104785646!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba85f3a13cd79f1%3A0x618c4b40b43038e6!2sVadavalli%2C%20Coimbatore%2C%20Tamil%20Nadu%20641041!5e0!3m2!1sen!2sin!4v1725308826893!5m2!1sen!2sin"
                        ></iframe>
                    </Box>
                </Card>
            </Container>

            <Modal open={modal.open} onClose={closeModal}>
                <ModalContainer>
                    <Typography variant="h6" gutterBottom>
                        {modal.title}
                    </Typography>
                    <Typography variant="body1" gutterBottom component="div">
                        {modal.message}
                    </Typography>
                    <Button variant="contained" color="primary" onClick={closeModal} sx={{ mt: 2 }}>
                        Close
                    </Button>
                </ModalContainer>
            </Modal>
        </Box>
    );
};

export default Contact;
